import React from 'react';
import { Box, Typography, Grid, Divider } from '@mui/material';
import {
  useUserContextController,
  setConnectedAccounts
} from '../../context/UserContext';
import IntegrationCard from '../../components/integrations/IntegrationCard';
import HubSpotLogo from '../../assets/hubspot-96.png';
import SalesforceLogo from '../../assets/SalesforceLogo.svg';
import LemlistLogo from '../../assets/LemlistLogo.png';
import OutreachLogo from '../../assets/OutreachLogo.png';
import { fetchConnectedAccounts } from '../../api/connector';

export type Integration = {
  id: string;
  name: string;
  logo: string;
};

export type IntegrationGroup = {
  label: string;
  description: string;
  integrations: Integration[];
};

const INTEGRATION_GROUPS: IntegrationGroup[] = [
  {
    label: 'CRM',
    description: 'Connect one CRM to sync contacts and accounts.',
    integrations: [
      { id: 'salesforce_rest_api', name: 'Salesforce', logo: SalesforceLogo },
      { id: 'hubspot', name: 'HubSpot', logo: HubSpotLogo }
    ]
  },
  {
    label: 'Sequencer & Outreach',
    description: 'Connect one outreach tool for email campaigns.',
    integrations: [
      { id: 'lemlist', name: 'Lemlist', logo: LemlistLogo },
      { id: 'outreach', name: 'Outreach', logo: OutreachLogo }
    ]
  }
];

const IntegrationsPage: React.FC = () => {
  const [userContext, userDispatch] = useUserContextController();

  const refreshConnectedAccounts = async () => {
    if (!userContext.company?.org_id) return;
    const accounts = await fetchConnectedAccounts();
    setConnectedAccounts(userDispatch, accounts);
  };

  const isOtherInGroupConnected = (
    integrationId: string,
    groupIntegrations: Integration[]
  ) => {
    return groupIntegrations.some(
      (i) =>
        i.id !== integrationId &&
        userContext.connectedAccounts.some(
          (acc) => acc.app === i.id && acc.connectionId
        )
    );
  };

  return (
    <Box
      sx={{
        padding: '24px',
        width: '100%',
        maxWidth: '800px',
        boxSizing: 'border-box'
      }}
    >
      <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 600 }}>
        Integrations
      </Typography>
      <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
        Connect your favorite tools and platforms to enhance your workflow and
        data synchronization.
      </Typography>

      {INTEGRATION_GROUPS.map((group) => (
        <Box key={group.label} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {group.label}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {group.description}
          </Typography>
          <Grid container spacing={3}>
            {group.integrations.map((integration) => {
              const connection = userContext.connectedAccounts.find(
                (acc) => acc.app === integration.id
              );
              const otherInGroupConnected = isOtherInGroupConnected(
                integration.id,
                group.integrations
              );
              const isDisabled =
                !userContext.company?.org_id ||
                (!connection && otherInGroupConnected);
              const disabledReason =
                !connection &&
                otherInGroupConnected &&
                userContext.company?.org_id
                  ? `Another ${group.label} integration is already connected. Disconnect it first to connect ${integration.name}.`
                  : undefined;

              return (
                <Grid item xs={12} sm={6} md={3} key={integration.id}>
                  <IntegrationCard
                    id={integration.id}
                    name={integration.name}
                    logo={integration.logo}
                    isConnected={!!connection}
                    connectionId={connection?.connectionId}
                    organizationId={userContext.company?.org_id || ''}
                    refreshConnectedAccounts={refreshConnectedAccounts}
                    disabled={isDisabled}
                    disabledReason={disabledReason}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}

      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography
          variant="body2"
          sx={{ textAlign: 'center', color: 'text.secondary' }}
        >
          More integrations coming soon! Have a specific integration in mind?
          Let us know.
        </Typography>
      </Box>
    </Box>
  );
};

export default IntegrationsPage;
